// Forespørsel til produsent — admin-panelet for «spor B» (FORESPORSEL-PLAN.md).
//
// Bestillingssporet trenger ingen UI utover «Kopier leveranselenke»: én ordre,
// én produsent. En forespørsel går til flere samtidig, og da må admin kunne
//
//   1. velge hvilke produsenter som skal få den,
//   2. se fastprisene som kommer inn ved siden av hverandre, og
//   3. velge én — eller se at noen har overtatt kunden eksklusivt.
//
// Panelet er selvstendig: det tar bare en orderId og henter alt selv, så det kan
// monteres på både den klassiske ordresiden og /bestillinger-ny uten at de to
// trenger å vite noe om forespørsler.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import LinkIcon from "@mui/icons-material/Link";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { notifyCenter } from "utils/toast";
import StatusChangeDialog from "components/StatusChangeDialog";
import { listVendorQuotes, selectVendorQuote, cancelVendorRequest } from "api/apiService";
import VendorRequestCompose from "./Compose";

// Samme ordlyd som produsenten ser på /leveranse-siden, så admin og produsent
// snakker om det samme når telefonen ringer.
const STATE_LABEL = {
  sent: { text: "Sendt – venter på svar", color: "default" },
  priced: { text: "Fastpris mottatt", color: "info" },
  taken_over: { text: "Overtatt kunden", color: "success" },
  declined: { text: "Avslått", color: "error" },
  selected: { text: "Valgt", color: "success" },
  not_selected: { text: "Ikke valgt", color: "default" },
  cancelled: { text: "Trukket tilbake", color: "default" },
};

// Samme ordlyd som gebyrsida (layouts/vendorFees), så de to ikke gliker fra hverandre.
const FEE_LABEL = {
  pending: "Ikke fakturert",
  invoiced: "Fakturert",
  paid: "Betalt",
  waived: "Frafalt",
};
const FEE_COLOR = { pending: "warning", invoiced: "info", paid: "success", waived: "default" };

const nok = (n) =>
  n == null ? "—" : `${Number(n).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} kr`;

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function VendorRequestPanel({ orderId, onOrderChanged }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  // «Velg denne»: admin setter kundens endelige pris i samme steg (s. 5 pkt. 6).
  const [choosing, setChoosing] = useState(null); // quote id
  const [finalPrice, setFinalPrice] = useState("");
  // Etter valget åpnes statusdialogen på «Klar til betaling», så e-posten med
  // betalingslenken kan leses gjennom og sendes — samme vei som en vanlig ordre.
  const [statusOpen, setStatusOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const quotes = await listVendorQuotes(orderId);
      if (quotes?.ok) setData(quotes);
    } catch (e) {
      console.error("VendorRequestPanel load:", e);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const quotes = data?.quotes || [];
  const takenOver = quotes.find((q) => q.state === "taken_over");
  const selected = quotes.find((q) => q.state === "selected");
  // «Trekk tilbake» gjelder bare ubesvarte — fastpriser står.
  const open = quotes.filter((q) => q.state === "sent");
  const onSent = async () => {
    setComposeOpen(false);
    await load();
    // Utsendingen setter ordrestatus til «Venter på tilbud».
    if (onOrderChanged) await onOrderChanged();
  };

  const choose = async (quoteId) => {
    const total = Number(String(finalPrice).replace(/\s/g, "").replace(",", "."));
    if (!Number.isFinite(total) || total <= 0) {
      notifyCenter("error", "Skriv inn kundens endelige pris.");
      return;
    }
    setBusy(true);
    try {
      const res = await selectVendorQuote(orderId, quoteId, total);
      if (res?.ok) {
        notifyCenter("success", "Produsent valgt og pris satt. Send betalingen til kunden.");
        setChoosing(null);
        await load();
        // Valget setter order.vendorId og totalen. Uten dette står kortene
        // igjen med det gamle til noen laster siden på nytt.
        if (onOrderChanged) await onOrderChanged();
        setStatusOpen(true);
      } else {
        const MSG = {
          invalid_final_price: "Kundens endelige pris er ikke et gyldig beløp.",
          already_taken_over: "En produsent har tatt over forespørselen i mellomtiden.",
          quote_not_priced: "Fastprisen er ikke lenger åpen for valg.",
          final_price_failed: "Kunne ikke sette prisen. Ingenting er endret — prøv igjen.",
        };
        notifyCenter("error", MSG[res?.error] || "Kunne ikke velge denne fastprisen.");
      }
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const res = await cancelVendorRequest(orderId);
      if (res?.ok) {
        notifyCenter("success", `${res.cancelled} åpen(e) forespørsel(er) trukket tilbake.`);
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      notifyCenter("success", "Forespørselslenke kopiert.");
    } catch {
      notifyCenter("error", "Kunne ikke kopiere lenken.");
    }
  };

  if (loading) {
    return (
      <Card sx={{ p: 3, mb: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={22} />
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <SoftBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <SoftBox>
          <SoftTypography variant="h6" fontWeight="bold">
            Forespørsel til produsent
          </SoftTypography>
          <SoftTypography variant="caption" color="text">
            Hent inn fastpris fra flere produsenter, eller la én overta kunden mot
            formidlingsgebyr ({nok(data?.fee_amount)} eks. mva.).
          </SoftTypography>
        </SoftBox>
        <SoftBox display="flex" gap={1} flexWrap="wrap">
          {/* Kundeinfo, tillegg, betongkvalitet, volum og leveringsopplysninger
              redigeres på ordresiden (Utvikling | BMAx s. 5 pkt. 2). Én knapp dit
              framfor et parallelt skjema her, som ville glidd fra det ekte. */}
          {!takenOver && (
            <SoftButton
              size="small"
              color="secondary"
              variant="outlined"
              onClick={() => navigate(`/bestillinger/edit-details/${orderId}`)}
            >
              Rediger ordredetaljer
            </SoftButton>
          )}
          {!takenOver && !selected && (
            <SoftButton
              size="small"
              color="info"
              variant={composeOpen ? "outlined" : "gradient"}
              onClick={() => setComposeOpen((v) => !v)}
            >
              {composeOpen ? "Avbryt" : quotes.length ? "Send til flere" : "Send forespørsel"}
            </SoftButton>
          )}
        </SoftBox>
      </SoftBox>

      {/* ------------------------------ overtatt ------------------------------ */}
      {takenOver && (
        <SoftBox
          mt={2}
          p={2}
          sx={{ backgroundColor: "#e7f6ed", border: "1px solid #b7e3c8", borderRadius: 2 }}
        >
          <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#0f8a43" }}>
            {takenOver.vendor_name} har overtatt forespørselen
          </SoftTypography>
          <SoftTypography variant="caption" display="block" color="text" mt={0.5}>
            Kundens kontaktinformasjon er utlevert til produsenten, og de øvrige er lukket ute.
          </SoftTypography>
          <SoftBox mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <SoftTypography variant="caption" color="text">
              Formidlingsgebyr: <strong>{nok(takenOver.fee_amount)} eks. mva.</strong>
            </SoftTypography>
            <Chip
              size="small"
              variant="outlined"
              label={FEE_LABEL[takenOver.fee_status] || takenOver.fee_status}
              color={FEE_COLOR[takenOver.fee_status] || "default"}
            />
            {takenOver.fee_invoice_ref && (
              <SoftTypography variant="caption" color="text">
                Fakturanr. {takenOver.fee_invoice_ref}
              </SoftTypography>
            )}
            <SoftButton
              size="small"
              variant="text"
              color="info"
              onClick={() => navigate("/formidlingsgebyr")}
            >
              Åpne gebyroversikten
            </SoftButton>
          </SoftBox>
        </SoftBox>
      )}

      {/* ------------------------------- send ny ------------------------------ */}
      {composeOpen && (
        <SoftBox mt={2}>
          <Divider />
          <VendorRequestCompose orderId={orderId} onSent={onSent} />
          <Divider />
        </SoftBox>
      )}

      {/* ------------------------------ oversikt ------------------------------ */}
      {!quotes.length ? (
        <SoftBox mt={2}>
          <SoftTypography variant="caption" color="text">
            Ingen forespørsel sendt på denne ordren ennå. Bruk dette når ordren ikke kan prises
            automatisk — vanskelig adkomst, større volum og lignende.
          </SoftTypography>
        </SoftBox>
      ) : (
        <SoftBox mt={2}>
          {data?.best_price != null && (
            <SoftTypography variant="caption" color="text" display="block" mb={1}>
              Laveste fastpris så langt: <strong>{nok(data.best_price)} eks. mva.</strong>
            </SoftTypography>
          )}

          {quotes.map((q) => {
            const isBest = q.state === "priced" && q.price_ex_vat != null && q.price_ex_vat === data?.best_price;
            const label = STATE_LABEL[q.state] || { text: q.state, color: "default" };
            return (
              <SoftBox
                key={q.id}
                mb={1.5}
                p={2}
                sx={{
                  border: `1px solid ${isBest ? "#1d4ed8" : "#e6ecf3"}`,
                  borderRadius: 2,
                  backgroundColor: isBest ? "rgba(29,78,216,0.04)" : "transparent",
                }}
              >
                <SoftBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <SoftTypography variant="button" fontWeight="bold">
                      {q.vendor_name}
                    </SoftTypography>
                    <Chip size="small" label={label.text} color={label.color} variant="outlined" />
                    {q.expired && ["sent", "priced"].includes(q.state) && (
                      <Chip size="small" label="Frist utløpt" color="warning" variant="outlined" />
                    )}
                    {isBest && <Chip size="small" label="Laveste pris" color="primary" />}
                  </SoftBox>
                  <SoftBox display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Kopier produsentens egen forespørselslenke">
                      <SoftButton size="small" variant="outlined" color="secondary" onClick={() => copy(q.link)}>
                        <LinkIcon sx={{ fontSize: 15, mr: 0.5 }} />
                        Lenke
                      </SoftButton>
                    </Tooltip>
                    {q.state === "priced" && !takenOver && !selected && choosing !== q.id && (
                      <SoftButton
                        size="small"
                        color="success"
                        variant="gradient"
                        disabled={busy}
                        onClick={() => {
                          setChoosing(q.id);
                          setFinalPrice("");
                        }}
                      >
                        Velg denne
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>

                <SoftBox mt={1} display="flex" gap={3} flexWrap="wrap">
                  <SoftTypography variant="caption" color="text">
                    Fastpris:{" "}
                    <strong>
                      {q.price == null ? "—" : `${nok(q.price)} ${q.price_inc_vat ? "inkl." : "eks."} mva.`}
                    </strong>
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    Svart: <strong>{fmt(q.responded_at)}</strong>
                  </SoftTypography>
                  {q.deadline && (
                    <SoftTypography variant="caption" color="text">
                      Frist: <strong>{fmt(q.deadline)}</strong>
                    </SoftTypography>
                  )}
                  {!q.vendor_email && (
                    <SoftTypography variant="caption" sx={{ color: "#b26a00" }}>
                      Ingen e-postadresse – lenken må sendes manuelt
                    </SoftTypography>
                  )}
                </SoftBox>

                {choosing === q.id && (
                  <SoftBox
                    mt={1.5}
                    p={1.5}
                    sx={{ backgroundColor: "#f4f6f9", borderRadius: 2, border: "1px solid #e6ecf3" }}
                  >
                    <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                      Endelig pris til kunden (kr inkl. mva.)
                    </SoftTypography>
                    <SoftBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <SoftBox sx={{ width: { xs: "100%", sm: 200 } }}>
                        <SoftInput
                          value={finalPrice}
                          inputMode="decimal"
                          placeholder="F.eks. 32 500"
                          onChange={(e) => setFinalPrice(e.target.value)}
                        />
                      </SoftBox>
                      <SoftButton
                        size="small"
                        color="success"
                        variant="gradient"
                        disabled={busy}
                        onClick={() => choose(q.id)}
                      >
                        {busy ? "Lagrer…" : "Velg og sett pris"}
                      </SoftButton>
                      <SoftButton size="small" variant="text" color="secondary" onClick={() => setChoosing(null)}>
                        Avbryt
                      </SoftButton>
                    </SoftBox>
                    <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                      Produsentens fastpris er {nok(q.price)} {q.price_inc_vat ? "inkl." : "eks."} mva. De
                      andre produsentene får beskjed om at forespørselen er avsluttet. Deretter åpnes
                      «Klar til betaling» med e-posten til kunden.
                    </SoftTypography>
                  </SoftBox>
                )}

                {q.terms && (
                  <SoftBox mt={1}>
                    <SoftTypography variant="caption" fontWeight="bold" display="block">
                      Vilkår fra produsent
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" sx={{ whiteSpace: "pre-wrap" }}>
                      {q.terms}
                    </SoftTypography>
                  </SoftBox>
                )}
                {q.note && (
                  <SoftBox mt={1}>
                    <SoftTypography variant="caption" fontWeight="bold" display="block">
                      Notat
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" sx={{ whiteSpace: "pre-wrap" }}>
                      {q.note}
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            );
          })}

          {!!open.length && !takenOver && (
            <SoftButton size="small" variant="text" color="error" disabled={busy} onClick={withdraw}>
              Trekk tilbake {open.length} åpen(e) forespørsel(er)
            </SoftButton>
          )}
        </SoftBox>
      )}

      <StatusChangeDialog
        open={statusOpen}
        orderId={orderId}
        initialStatus="pending"
        currentStatus="awaiting-quote"
        onClose={() => setStatusOpen(false)}
        onChanged={onOrderChanged}
      />
    </Card>
  );
}

VendorRequestPanel.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // Kalles når panelet har endret selve ordren (produsent eller status), så
  // ordresiden rundt kan hente seg selv på nytt.
  onOrderChanged: PropTypes.func,
};

VendorRequestPanel.defaultProps = {
  onOrderChanged: null,
};

export default VendorRequestPanel;
